import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGS } from "../i18n";

/**
 * Language switcher — two variants:
 *   variant="compact" (default) → globe icon + current label, dropdown menu
 *   variant="pills"             → side-by-side "EN | मराठी" toggle
 *
 * Both persist the choice via i18next's LanguageDetector (localStorage:"uid_lang").
 */
export default function LanguageSwitcher({ variant = "compact", className = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = SUPPORTED_LANGS.find((l) => l.code === (i18n.resolvedLanguage || i18n.language))
                  || SUPPORTED_LANGS[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const change = (code) => {
    if (code !== current.code) i18n.changeLanguage(code);
    setOpen(false);
  };

  if (variant === "pills") {
    return (
      <div className={`inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] p-0.5 ${className}`} role="tablist" aria-label="Language">
        {SUPPORTED_LANGS.map((l) => {
          const active = l.code === current.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => change(l.code)}
              role="tab"
              aria-selected={active}
              data-testid={`lang-pill-${l.code}`}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${
                active
                  ? "bg-blue-500/25 text-blue-100 shadow-inner"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {l.native}
            </button>
          );
        })}
      </div>
    );
  }

  // compact variant
  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/85 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        data-testid="lang-switcher-button"
      >
        <Globe className="h-3.5 w-3.5" />
        <span className="min-w-[38px] text-left">{current.native}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1.5 w-40 z-50 rounded-xl border border-white/15 bg-[#0F1626] shadow-2xl overflow-hidden text-sm"
          data-testid="lang-switcher-menu"
        >
          {SUPPORTED_LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => change(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/[0.06] transition-colors ${
                    active ? "text-blue-200" : "text-white/85"
                  }`}
                >
                  <span>
                    <span className="font-semibold">{l.native}</span>
                    {l.label !== l.native && (
                      <span className="ml-1.5 text-[10px] text-white/40">{l.label}</span>
                    )}
                  </span>
                  {active && <Check className="h-3.5 w-3.5 text-blue-300" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
